import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightOutputFormat } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import type { DocumentType } from "@aws-sdk/smithy-client";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect } from "react";
import { useStore } from "zustand";

import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { useInsightPackagePreviewDownloadAction } from "../../../../hooks/insightPackages/useInsightPackagePreviewDownloadAction";
import { useInsightIdentifierEffect } from "../../../../hooks/misc/useInsightIdentifierEffect";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../store/insightDetails";
import { reportIssueStore } from "../../../../store/reportIssue";
import { workbenchStore } from "../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import type { DownloadAsset } from "../../../../types/common";
import { InsightPreviewFileFormats } from "../../../../types/insightPreviewFileFormats";
import { isLoading, isSuccess } from "../../../../utils/api.utils";
import {
    logViewInsightDetailsClick,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { buildAPBDeepLink } from "../../../common/insights.helper";
import { StyledActionsContainer } from "../../commonStyles";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

import { InsightPackagesAdditionalDetails } from "./InsightPackagesAdditionalDetails";

export interface InsightPackagesCardActionProps {
    recommendationId: string;
    details: CardDetail[];
    marketplaceId: string;
    advertiserId: string;
    title: string;
    insightType: InsightType;
    description?: string;
    index?: number;
    metadata?: DocumentType;
}

export const InsightPackagesCardAction: React.FunctionComponent<
    InsightPackagesCardActionProps
> = ({
    recommendationId,
    title,
    details,
    marketplaceId,
    advertiserId,
    insightType,
    description,
    index,
    metadata,
}) => {
    const [useUnifiedSearch, thumbsFeedbackCard] = useStore(
        FeatureFlagsStore,
        (state) => [state.unifiedSearch, state.thumbsFeedbackCard],
    );

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);
    const linkedApbs: number =
        metadata!["linkedApbs"] > 0 ? metadata!["linkedApbs"] : undefined;
    const entityType = metadata!["entityType"];
    const entityTypeId = metadata!["entityTypeId"];
    const entityId = metadata!["entityId"];
    const apbRules = metadata!["apbRules"];

    const apbLink = React.useMemo(
        () =>
            buildAPBDeepLink(
                entityType,
                entityTypeId,
                marketplaceId,
                entityId,
                apbRules,
            ),
        [entityType, entityTypeId, marketplaceId, entityId, apbRules],
    );
    const [setInsightDetails, setAsyncStatus] = useStore(
        insightDetailsStore,
        (state) => [state.setInsightDetails, state.setAsyncStatus],
    );
    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const insightsData = filters[contentSubType.provider].data;

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const disabled = Object.values({
        recommendationId,
        marketplaceId,
    }).some((val) => isEmpty(val));

    const requestDownload = {
        advertiserIdType: "SALESFORCE",
        advertiserId,
        marketplaceId,
        identifier: recommendationId,
        outputFormat: InsightOutputFormat.PPTX,
    };

    const [downloadState, downloadAction] =
        useInsightPackagePreviewDownloadAction(requestDownload);

    const requestPreview = {
        ...requestDownload,
        outputFormat: InsightOutputFormat.PDF,
    };

    const [previewState, previewAction] =
        useInsightPackagePreviewDownloadAction(requestPreview);

    useEffect(() => {
        setAsyncStatus(previewState.status);
    }, [previewState, setAsyncStatus]);

    const handleInsightDetails = useCallback(async (): Promise<void> => {
        logViewInsightDetailsClick(
            recommendationId,
            (index ?? 0) + 1,
            insightsData.length,
            undefined,
            useUnifiedSearch,
        );
        const standalonePackagePreviewData = await previewAction.execute();
        setPreviewAsset(standalonePackagePreviewData as DownloadAsset);

        const standalonePackageDownloadData = await downloadAction.execute();
        setDownloadAsset(standalonePackageDownloadData as DownloadAsset);
    }, []);

    const handleApb = () => {
        logWithTakt(TELEMETRY_ID.CAIRE_LINK_TO_APB, {
            insightType,
            recommendationId,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.CAIRE_LINK_TO_APB,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: {
                    contentProvider: insightType,
                    contentType: "",
                    contentId: recommendationId,
                },
            },
        );
        window.open(apbLink, "_blank");
    };

    React.useEffect(() => {
        if (isSuccess(previewState.status)) {
            updateReportIssueContextData({
                identifier: recommendationId,
                title,
                insightType,
            });
            setInsightDetails({
                open: true,
                info: {
                    title,
                    details,
                    previewFileFormat: InsightPreviewFileFormats.PDF,
                    insightType,
                    identifier: recommendationId,
                    previewAsset,
                    downloadAsset,
                    description,
                    previewLoading: isLoading(previewState.status),
                    additionalDetails: (
                        <InsightPackagesAdditionalDetails
                            linkedApbs={linkedApbs}
                            apbLink={apbLink}
                            recommendationId={recommendationId}
                            insightType={insightType}
                        />
                    ),
                },
            });
        }
    }, [
        previewAsset,
        downloadAsset,
        previewState.status,
        downloadState.status,
    ]);

    useInsightIdentifierEffect(recommendationId, handleInsightDetails);
    const numSelectedSlides = getSlidesSelected(recommendationId).length;

    return (
        <StyledActionsContainer>
            <TextButton
                disabled={disabled}
                onClick={() => void handleInsightDetails()}
            >
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={recommendationId}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={recommendationId}
                    insightType={insightType}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
